import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Grid, Typography } from "@material-ui/core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import LoadingData from "components/Loading/LoadingData";
import MaskAndValid from "utils/maskAndValid";
import ModalItensNota from "./ModalItensNota";

function formatBrl(value) {
  const num = Number(value) || 0;
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatInscricaoEstadual(value) {
  if (!value || String(value).trim().toUpperCase() === "ISENTO") {
    return "Não contribuinte";
  }

  return value;
}

function unwrapResult(response) {
  if (response?.Success === false) {
    throw new Error(
      response?.ErrorMessage ||
        response?.ValidationMessage ||
        "Erro na requisição.",
    );
  }
  return response?.Result ?? response?.result ?? response;
}

export default function FormNovaSolicitacao({ onBack, onCreated }) {
  const snackbar = useSnackbar();

  const [options, setOptions] = useState({
    MotivosDevolucao: [],
    MotivosLancamento: [],
    Origens: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [numeroNota, setNumeroNota] = useState("");
  const [motivoDevolucao, setMotivoDevolucao] = useState(null);
  const [motivoLancamento, setMotivoLancamento] = useState(null);
  const [origem, setOrigem] = useState(null);

  const [searchingInvoice, setSearchingInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [openItens, setOpenItens] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function loadOptions() {
      setLoadingOptions(true);
      try {
        const res = await Api.get("options", "internalReturn", "", true);
        const data = unwrapResult(res);
        setOptions({
          MotivosDevolucao: (data?.MotivosDevolucao || []).map((m) => ({
            label: `${m.Codigo} - ${m.Descricao}`,
            value: m.Codigo,
          })),
          MotivosLancamento: (data?.MotivosLancamento || []).map((m) => ({
            label: `${m.Codigo} - ${m.Descricao}`,
            value: m.Codigo,
          })),
          Origens: (data?.Origens || []).map((m) => ({
            label: m.Descricao || m.Codigo,
            value: m.Codigo,
          })),
        });
      } catch (error) {
        snackbar.showMessage(
          error?.message ||
            "Não foi possível carregar as opções do formulário.",
          { severity: "error" },
        );
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  const handleSearchInvoice = async () => {
    const nf = numeroNota.trim();
    if (!nf) {
      snackbar.showMessage("Informe o número da nota fiscal.", {
        severity: "warning",
      });
      return;
    }

    setSearchingInvoice(true);
    setInvoiceData(null);

    try {
      const res = await Api.get(
        "invoice",
        "internalReturn",
        `?numeroNota=${encodeURIComponent(nf)}`,
        true,
      );
      const data = unwrapResult(res);
      if (!data) {
        snackbar.showMessage("Nota fiscal não encontrada.", {
          severity: "info",
        });
        return;
      }
      setInvoiceData(data);
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Não foi possível consultar a nota fiscal.",
        { severity: "error" },
      );
    } finally {
      setSearchingInvoice(false);
    }
  };

  const handleSubmit = async () => {
    if (!invoiceData) {
      snackbar.showMessage("Consulte e valide a nota fiscal antes de salvar.", {
        severity: "warning",
      });
      return;
    }

    if (!motivoDevolucao?.value) {
      snackbar.showMessage("Motivo da devolução é obrigatório.", {
        severity: "warning",
      });
      return;
    }

    if (!motivoLancamento?.value) {
      snackbar.showMessage("Motivo do lançamento é obrigatório.", {
        severity: "warning",
      });
      return;
    }

    if (!origem?.value) {
      snackbar.showMessage("Mercadoria encontra-se em é obrigatório.", {
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);

    try {
      const body = {
        NumeroNota: Number(numeroNota),
        SerieNota: invoiceData.SerieNota,
        MotivoDevolucao: motivoDevolucao.value,
        MotivoLancamento: motivoLancamento.value,
        Origem: origem.value,
      };

      const res = await Api.post("create", "internalReturn", body, true);
      unwrapResult(res);
      snackbar.showMessage("Solicitação cadastrada com sucesso!", {
        severity: "success",
      });
      onCreated();
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Não foi possível cadastrar a solicitação.",
        { severity: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <LoadingData searching label="Carregando formulário..." hiddenIcon />
    );
  }

  return (
    <>
      <Box mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} color="primary">
          Voltar para lista
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            Nova Solicitação de Devolução Interna
          </Typography>
          <Box
            mb={3}
            p={2}
            style={{ background: "#fff8e1", borderRadius: 8 }}
          >
            <Typography variant="body2">
              Esta funcionalidade trata de <strong>devolução interna</strong>,
              sem solicitação originada pelo cliente.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <InputText
                label="Nota Fiscal"
                value={numeroNota}
                onChange={(e) => {
                  setNumeroNota(e.target.value.replace(/\D/g, "").slice(0, 10));
                  setInvoiceData(null);
                }}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              style={{
                display: "flex",
                justifyContent: "end",
                alignItems: "start",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearchInvoice}
                disabled={searchingInvoice || !numeroNota}
              >
                {searchingInvoice ? "Consultando..." : "Consultar Nota"}
              </Button>
            </Grid>
          </Grid>

          {invoiceData && (
            <Box
              mt={3}
              p={2}
              style={{ background: "#f8f9fe", borderRadius: 8 }}
            >
              <Typography
                variant="h4"
                gutterBottom
                style={{ marginBottom: 4 }}
              >
                Dados da Nota Fiscal
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 12 }}
              >
                Esta solicitação corresponde à devolução total da nota
                fiscal — não é possível selecionar itens individualmente.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Código ERP Cliente:</strong>{" "}
                    {invoiceData.CodigoErpCliente || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>CNPJ:</strong>{" "}
                    {MaskAndValid.maskCNPJ(invoiceData.Cnpj || "") || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Razão Social:</strong>{" "}
                    {invoiceData.RazaoSocial || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Inscrição estadual:</strong>{" "}
                    {formatInscricaoEstadual(invoiceData.InscricaoEstadual)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Volume:</strong> {invoiceData.Volume ?? 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Valor Total da Nota:</strong>{" "}
                    {formatBrl(invoiceData.ValorTotalNota)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => setOpenItens(true)}
                  >
                    Visualizar Itens da Nota
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <AutoCompleteField
                  label="Motivo da Devolução"
                  options={options.MotivosDevolucao}
                  value={motivoDevolucao}
                  onChange={setMotivoDevolucao}
                  optionLabelKey="label"
                  optionIdKey="value"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AutoCompleteField
                  label="Motivo do Lançamento"
                  options={options.MotivosLancamento}
                  value={motivoLancamento}
                  onChange={setMotivoLancamento}
                  optionLabelKey="label"
                  optionIdKey="value"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AutoCompleteField
                  label="Mercadoria encontra-se em"
                  options={options.Origens}
                  value={origem}
                  onChange={setOrigem}
                  optionLabelKey="label"
                  optionIdKey="value"
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Salvando..." : "Cadastrar Solicitação"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <ModalItensNota
        open={openItens}
        onClose={() => setOpenItens(false)}
        itens={invoiceData?.Itens}
        numeroNota={invoiceData?.NumeroNota}
      />
    </>
  );
}
